import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaTrash, FaMapMarkedAlt, FaPlus } from 'react-icons/fa'; // Importing react-icons

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Header = styled.h2`
  margin: 0 0 20px;
  font-size: 24px;
  color: #333;
  text-align: center;
`;

const CoordinateTableContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  scrollbar-width: thin; /* Firefox에서 얇은 스크롤바 */
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* 스크롤바 색상 */

  /* Chrome, Safari, Edge의 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 8px; /* 스크롤바의 너비 */
  }

  &::-webkit-scrollbar-track {
    background: transparent; /* 스크롤 트랙 색상 */
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3); /* 스크롤바 색상 */
    border-radius: 10px; /* 둥근 스크롤바 */
    border: 2px solid transparent;
    background-clip: padding-box;
  }
`;

const CoordinateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  th, td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  button {
    background-color: transparent;
    color: #007bff;
    border: none;
    padding: 5px;
    cursor: pointer;
    font-size: 18px;
  }

  button:hover {
    color: #0056b3;
  }
`;

const CoordinateInput = styled.input`
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  box-sizing: border-box;
  text-align: right;
`;

const OptionsSection = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OptionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
`;

const OptionLabel = styled.label`
  font-size: 14px;
  width: 150px;
  margin-bottom: 5px;
  text-align: left;
`;

const OptionInput = styled.input`
  margin-left: 10px;
  flex: 1;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  text-align: right;

  &[type="checkbox"] {
    width: 20px; 
    height: 20px; 
    transform: scale(1.5); 
    cursor: pointer;
    margin-left: 15px;
  }

  &[type="checkbox"]:checked {
    background-color: #007bff;
    border-color: #007bff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const ModalButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:active {
    background-color: #00408d;
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #d6d6d6;
    color: #a1a1a1;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled(ModalButton)`
  background-color: #6c757d;
  &:hover {
    background-color: #5a6268;
  }
  &:active {
    background-color: #4e555b;
  }
`;

const AddCoordinateButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.2s;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  &:hover {
    background-color: #218838;
  }

  &:active {
    background-color: #1e7e34;
    transform: scale(0.98);
  }

  i {
    margin-right: 5px;
  }
`;

const PolygonModal = ({ isOpen, onClose, onSave, mouseCoordinates, onGetLocation, offGetLocation }) => {
  const [coordinates, setCoordinates] = useState([{ lat: '', lng: '', radius: '' }]);
  const [lastLat, setLastLat] = useState('');
  const [lastLng, setLastLng] = useState('');
  const [fillColor, setFillColor] = useState('#FF0000');
  const [isFilled, setIsFilled] = useState(true);
  const [strokeColor, setStrokeColor] = useState('#FF0000');
  const [strokeOpacity, setStrokeOpacity] = useState(0.8);
  const [strokeWeight, setStrokeWeight] = useState(2);
  const [polygonName, setPolygonName] = useState('');
  const [shapeType, setShapeType] = useState('polygon');
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [circleStrokeColor, setCircleStrokeColor] = useState('#FF0000');
  const [circleFillColor, setCircleFillColor] = useState('#FF0000');

  const [clickCoordinates, setClickCoordinates] = useState(mouseCoordinates || false);
  const [mouseIndex, setMouseIndex] = useState(0);

  const addCoordinate = () => {
    setCoordinates([...coordinates, { lat: lastLat, lng: lastLng, radius: 0 }]);
    setLastLat('');
    setLastLng('');
  };

  const handleInputChange = (index, field, value) => {
    const newCoordinates = [...coordinates];
    newCoordinates[index][field] = value;
    setCoordinates(newCoordinates);

    if (field === 'lat') {
      setLastLat(value);
    } else if (field === 'lng') {
      setLastLng(value);
    }
  };

  const handleDeleteCoordinate = (index) => {
    const newCoordinates = coordinates.filter((_, i) => i !== index);
    setCoordinates(newCoordinates); // Remove the coordinate
  };

  useEffect(() => {
    if (mouseCoordinates?.lat && mouseCoordinates?.lng && mouseIndex !== null) {
  
      offGetLocation();
      setClickCoordinates(mouseCoordinates);
      const fetchedLat = mouseCoordinates.lat;
      const fetchedLng = mouseCoordinates.lng;
  
      if (fetchedLat && fetchedLng) {
  
        // Update only the coordinate at mouseIndex
        setCoordinates((prevState) =>
          prevState.map((coord, index) =>
            index === mouseIndex
              ? { ...coord, lat: fetchedLat, lng: fetchedLng, radius: coord.radius || 0 }
              : coord
          )
        );
  
        // Optionally update last known coordinates
        setLastLat(fetchedLat);
        setLastLng(fetchedLng);
      }
    }
  }, [mouseCoordinates]); // Make sure mouseIndex is included in dependencies
  
  const handleGetLocation = (coordIndex) => {
    onGetLocation(coordIndex);
    setMouseIndex(coordIndex);
  };

  const handleSave = () => {
    const validCoords = coordinates
      .map(coord => ({
        lat: parseFloat(coord.lat),
        lng: parseFloat(coord.lng),
        radius: radiusEnabled ? parseFloat(coord.radius) || 0 : 0 // Include radius if enabled
      }))
      .filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng) && isFinite(coord.lat) && isFinite(coord.lng));

    if (validCoords.length >= 1) {
      onSave({
        name: polygonName,
        coords: validCoords,
        fillColor,
        isFilled: shapeType === 'polygon' ? isFilled : false,
        radiusEnabled: radiusEnabled,
        strokeColor,
        strokeOpacity,
        strokeWeight,
        type: shapeType,
        circleStrokeColor, // Pass circle stroke color
        circleFillColor, // Pass circle fill color
      });
      onClose();
    } else {
      alert("Please enter at least 1 valid coordinates.");
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <Header>Edit Polygon/Line</Header>
        <CoordinateTableContainer>
          <CoordinateTable>
            <thead>
              <tr>
                <th>Latitude</th>
                <th>Longitude</th>
                {radiusEnabled && <th>Radius (m)</th>} {/* Show Radius only when enabled */}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {coordinates.map((coord, index) => (
                <tr key={index}>
                  <td>
                    <CoordinateInput
                      type="number"
                      placeholder="Latitude"
                      value={coord.lat}
                      onChange={(e) => handleInputChange(index, 'lat', e.target.value)}
                    />
                  </td>
                  <td>
                    <CoordinateInput
                      type="number"
                      placeholder="Longitude"
                      value={coord.lng}
                      onChange={(e) => handleInputChange(index, 'lng', e.target.value)}
                    />
                  </td>
                  {radiusEnabled && (
                    <td>
                      <CoordinateInput
                        type="number"
                        placeholder="Radius (m)"
                        value={coord.radius !== undefined ? coord.radius : 0}  // undefined일 때만 기본값으로 1 설정
                        onChange={(e) => handleInputChange(index, 'radius', e.target.value)}
                      />
                    </td>
                  )}
                  <td>
                    <button onClick={() => handleDeleteCoordinate(index)}>
                      <FaTrash />
                    </button>
                    <button onClick={() => handleGetLocation(index)} style={{ marginLeft: "5px" }}>
                      <FaMapMarkedAlt />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </CoordinateTable>
        </CoordinateTableContainer>
        <AddCoordinateButton onClick={addCoordinate}>
          <FaPlus /> {/* Add Icon */}
        </AddCoordinateButton>
        <OptionsSection>
          <OptionGroup>
            <OptionLabel>
              Shape Type:
            </OptionLabel>
            <OptionInput
              as="select"
              value={shapeType}
              onChange={(e) => setShapeType(e.target.value)}
            >
              <option value="polygon">Polygon</option>
              <option value="line">Line</option>
            </OptionInput>
          </OptionGroup>
          <OptionGroup>
            <OptionLabel>
              Polygon Name:
            </OptionLabel>
            <OptionInput
              type="text"
              value={polygonName}
              onChange={(e) => setPolygonName(e.target.value)}
              placeholder="Enter polygon name"
            />
          </OptionGroup>

          {shapeType === 'polygon' && (
            <OptionGroup>
              <OptionLabel>
                Fill Color:
              </OptionLabel>
              <OptionInput
                type="color"
                value={fillColor}
                onChange={(e) => setFillColor(e.target.value)}
              />
              <OptionLabel>
                Fill Polygon:
              </OptionLabel>
              <OptionInput
                type="checkbox"
                checked={isFilled}
                onChange={(e) => setIsFilled(e.target.checked)}
              />
            </OptionGroup>
          )}

          <OptionGroup>
            <OptionLabel>
              Stroke Color:
            </OptionLabel>
            <OptionInput
              type="color"
              value={strokeColor}
              
              style={{ width: "120px" }}
              onChange={(e) => setStrokeColor(e.target.value)}
            />
            <OptionLabel>
              Stroke Opacity:
            </OptionLabel>
            <OptionInput
              type="number"
              step="0.1"
              min="0"
              max="1"
              value={strokeOpacity}
              onChange={(e) => setStrokeOpacity(parseFloat(e.target.value))}
            />
            <OptionLabel>
              Stroke Weight:
            </OptionLabel>
            <OptionInput
              type="number"
              step="1"
              min="1"
              value={strokeWeight}
              onChange={(e) => setStrokeWeight(parseInt(e.target.value))}
            />
          </OptionGroup>

          <OptionGroup>
            <OptionLabel>
              Enable Radius:
            </OptionLabel>
            <OptionInput
              type="checkbox"
              checked={radiusEnabled}
              onChange={(e) => setRadiusEnabled(e.target.checked)}
            />
          </OptionGroup>

          {radiusEnabled && (
            <>
              <OptionGroup>
                <OptionLabel>
                  Circle Stroke Color:
                </OptionLabel>
                <OptionInput
                  type="color"
                  value={circleStrokeColor}
                  onChange={(e) => setCircleStrokeColor(e.target.value)}
                />
              </OptionGroup>
              <OptionGroup>
                <OptionLabel>
                  Circle Fill Color:
                </OptionLabel>
                <OptionInput
                  type="color"
                  value={circleFillColor}
                  onChange={(e) => setCircleFillColor(e.target.value)}
                />
              </OptionGroup>
            </>
          )}
        </OptionsSection>
        
        <ButtonGroup>
          <ModalButton onClick={handleSave}>Save</ModalButton>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PolygonModal;
